import {
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CatalogueApiService } from '@angular-workspace/services';
import { DatasetHomeComponent } from './dataset/dataset-home/dataset-home.component';
import { NgsildHomeComponent } from './dataset/ngsild/ngsild-home/ngsild-home.component';
import {
  UploadFilesComponent,
  type NgsildUploadRequestType,
} from './dataset/ngsild/upload-files/upload-files.component';
import { OgcUploadFilesComponent } from './dataset/ogc/upload-files/upload-files.component';
import { StacCollectionOnboardingComponent } from './dataset/ogc/stac-collection-onboarding/stac-collection-onboarding.component';
import { MetadataJsonHomeComponent } from './dataset/metadata-json/metadata-json-home.component';
import type { DatasetOnboardingSelection } from './dataset/dataset-home/dataset-home.models';
import type { ResourceServerId } from './dataset/dataset-home/dataset-home.models';
import {
  getConnectionDetailsModalLedeKind,
  normalizeResourceServerId,
  resourceServerIdFromItemDetail,
  resourceServerIdFromKeys,
  type ConnectionDetailsModalLedeKind,
} from './dataset/resource-server.util';
import type { NgsildOnboardingResult } from './dataset/ngsild/ngsild.models';

/** Created dataset + resource server metadata for upload modal. */
type NgsildCreateCompleteResult = NgsildOnboardingResult & {
  resourceServerId: ResourceServerId;
  resourceServerKeys: string[];
};

type OnboardingView = 'dataset-select' | 'ngsild-manual' | 'json-upload';

/** Query param `flow` keeps wizard state after refresh. */
const FLOW_QUERY_KEY = 'flow';
const SERVER_QUERY_KEY = 'server';
const OGC_MODE_QUERY_KEY = 'ogcMode';
const FLOW_NGSILD = 'ngsild';
const FLOW_JSON_UPLOAD = 'json-upload';

@Component({
  selector: 'lib-onboarding-home',
  standalone: true,
  imports: [
    CommonModule,
    DatasetHomeComponent,
    NgsildHomeComponent,
    UploadFilesComponent,
    OgcUploadFilesComponent,
    StacCollectionOnboardingComponent,
    MetadataJsonHomeComponent,
  ],
  templateUrl: './onboarding-home.component.html',
  styleUrl: './onboarding-home.component.scss',
  host: {
    class: 'feature-onboarding-home-host',
  },
})
export class OnboardingHomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly catalogueApi = inject(CatalogueApiService);

  /** Org-admin publishes directly; provider submissions typically need approval. */
  readonly publisherRole = input<'org-admin' | 'provider'>('org-admin');

  readonly leafImageSrc = input('/assets-leaf.png');

  /** Catalogue listing path (app-relative, e.g. `/datasets`). */
  readonly catalogueDatasetsPath = input('/datasets');

  /** Dataset detail route prefix (app-relative, e.g. `/dataset` → `/dataset/:id`). */
  readonly datasetDetailPath = input('/dataset');

  readonly view = signal<OnboardingView>('dataset-select');
  readonly initialSelection = signal<DatasetOnboardingSelection | null>(null);
  readonly successModalVisible = signal(false);
  readonly uploadModalVisible = signal(false);
  readonly ogcUploadModalVisible = signal(false);
  readonly stacOnboardingModalVisible = signal(false);
  readonly ogcUploadLoadKey = signal(0);
  readonly publishSuccessModalVisible = signal(false);
  /** SFTP / API `asset/request` success (old upload flow). */
  readonly uploadRequestSuccessModalVisible = signal(false);
  readonly directPushSuccessModalVisible = signal(false);
  readonly uploadRequestType = signal<NgsildUploadRequestType | null>(null);
  readonly ngsildLastPayload = signal<NgsildCreateCompleteResult | null>(null);
  readonly ngsildCreatedItemId = signal<string | null>(null);
  readonly stacCollectionId = signal<string | null>(null);
  readonly stacItemId = signal<string | null>(null);

  /** Resolved when opening upload modal (from item API `resourceServers`). */
  readonly uploadModalResourceServer = signal<ResourceServerId>('ngsi-ld');

  /** OGC Server + Feature Onboarding uses GeoPackage upload modal. */
  readonly isOgcFeatureUploadFlow = computed(() => {
    const server =
      this.uploadModalResourceServer() ??
      this.ngsildLastPayload()?.resourceServerId ??
      this.initialSelection()?.resourceServer;
    return (
      server === 'ogc' &&
      this.initialSelection()?.ogcOnboardingMode === 'feature'
    );
  });

  /** OGC Server + Raster Onboarding uses STAC collection onboarding first. */
  readonly isOgcRasterUploadFlow = computed(() => {
    const server =
      this.uploadModalResourceServer() ??
      this.ngsildLastPayload()?.resourceServerId ??
      this.initialSelection()?.resourceServer;
    return (
      server === 'ogc' &&
      this.initialSelection()?.ogcOnboardingMode === 'raster'
    );
  });

  /** Resource server for the dataset just created (upload modal options). */
  readonly activeUploadResourceServer = computed(() => {
    const keys = this.ngsildLastPayload()?.resourceServerKeys ?? [];
    const fromKeys = resourceServerIdFromKeys(keys);
    if (fromKeys) return fromKeys;
    return (
      this.ngsildLastPayload()?.resourceServerId ??
      this.initialSelection()?.resourceServer ??
      'ngsi-ld'
    );
  });

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const flow = params.get(FLOW_QUERY_KEY);
        if (flow === FLOW_NGSILD) {
          this.view.set('ngsild-manual');
          this.restoreSelectionFromQueryParams(params, 'manual');
        } else if (flow === FLOW_JSON_UPLOAD) {
          this.view.set('json-upload');
          this.restoreSelectionFromQueryParams(params, 'json-upload');
        } else {
          this.view.set('dataset-select');
        }
      });
  }

  private restoreSelectionFromQueryParams(
    params: { get: (name: string) => string | null },
    metadataMode: DatasetOnboardingSelection['metadataMode'],
  ): void {
    const serverParam = params.get(SERVER_QUERY_KEY);
    if (!serverParam) return;

    const resourceServer = normalizeResourceServerId(serverParam);
    const ogcRaw = params.get(OGC_MODE_QUERY_KEY);
    const ogcOnboardingMode =
      ogcRaw === 'feature' || ogcRaw === 'raster' ? ogcRaw : undefined;

    this.initialSelection.set({
      resourceServer,
      metadataMode,
      ...(resourceServer === 'ogc' && ogcOnboardingMode
        ? { ogcOnboardingMode }
        : {}),
    });
  }

  onDatasetSelection(selection: DatasetOnboardingSelection): void {
    const server = selection.resourceServer;
    const supportedServer =
      server === 'ngsi-ld' || server === 'file-server' || server === 'gateway' || server === 'ogc';

    if (!supportedServer) {
      console.warn('Resource server not available for onboarding:', selection);
      return;
    }

    this.initialSelection.set(selection);

    const serverQueryParams = this.buildServerQueryParams(selection);

    if (selection.metadataMode === 'manual') {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          [FLOW_QUERY_KEY]: FLOW_NGSILD,
          step: 0,
          ...serverQueryParams,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.view.set('ngsild-manual');
      return;
    }

    if (selection.metadataMode === 'json-upload') {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          [FLOW_QUERY_KEY]: FLOW_JSON_UPLOAD,
          step: null,
          ...serverQueryParams,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.view.set('json-upload');
    }
  }

  private buildServerQueryParams(
    selection: DatasetOnboardingSelection,
  ): Record<string, string | null> {
    const params: Record<string, string | null> = {
      [SERVER_QUERY_KEY]: selection.resourceServer,
      [OGC_MODE_QUERY_KEY]:
        selection.resourceServer === 'ogc' && selection.ogcOnboardingMode
          ? selection.ogcOnboardingMode
          : null,
    };
    return params;
  }

  onNgsildBack(): void {
    this.initialSelection.set(null);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [FLOW_QUERY_KEY]: null,
        [SERVER_QUERY_KEY]: null,
        [OGC_MODE_QUERY_KEY]: null,
        step: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  readonly submitError = output<string>();
  readonly submitSuccess = output<string>();

  onNgsildComplete(result: NgsildCreateCompleteResult): void {
    this.ngsildLastPayload.set(result);
    this.ngsildCreatedItemId.set(result.itemId);
    this.stacCollectionId.set(result.stacCollectionId ?? null);
    this.stacItemId.set(result.stacItemId ?? null);
    this.successModalVisible.set(true);
  }

  onNgsildSubmitError(message: string): void {
    this.submitError.emit(message);
  }

  onStacOnboardingSuccess(message: string): void {
    if (/assets uploaded successfully/i.test(message)) {
      this.dismissStacOnboardingModal(false);
      this.onUploadPublishSuccess();
    }
    this.submitSuccess.emit(message);
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseModals(): void {
    if (this.directPushSuccessModalVisible()) {
      this.dismissDirectPushSuccessModal();
      return;
    }
    if (this.publishSuccessModalVisible()) {
      return;
    }
    if (this.ogcUploadModalVisible()) {
      this.dismissOgcUploadModal();
      return;
    }
    if (this.stacOnboardingModalVisible()) {
      this.dismissStacOnboardingModal();
      return;
    }
    if (this.uploadModalVisible()) {
      this.dismissUploadModal();
      return;
    }
    if (!this.successModalVisible()) return;
    this.dismissSuccessModal();
  }

  dismissSuccessModal(): void {
    this.successModalVisible.set(false);
  }

  dismissUploadModal(restoreSuccessModal = true): void {
    this.uploadModalVisible.set(false);
    // Restore success modal when user closes upload modal without completing upload
    if (restoreSuccessModal) {
      this.successModalVisible.set(true);
    }
  }

  dismissOgcUploadModal(restoreSuccessModal = true): void {
    this.ogcUploadModalVisible.set(false);
    // Restore success modal when user closes OGC upload modal without completing upload
    if (restoreSuccessModal) {
      this.successModalVisible.set(true);
    }
  }

  dismissStacOnboardingModal(restoreSuccessModal = true): void {
    this.stacOnboardingModalVisible.set(false);
    if (restoreSuccessModal) {
      this.successModalVisible.set(true);
    }
  }

  uploadModalFileFormat(): string {
    return this.ngsildLastPayload()?.payload.metadata.fileFormat?.trim() ?? '';
  }

  uploadModalDatasetLabel(): string {
    return this.ngsildLastPayload()?.payload.basic.title?.trim() ?? '';
  }

  uploadModalConnectionDetailsLedeKind(): ConnectionDetailsModalLedeKind {
    return getConnectionDetailsModalLedeKind(this.uploadModalResourceServer());
  }

  uploadModalDataSourceUrlEnabled(): boolean {
    const server = this.uploadModalResourceServer();
    // NGSI-LD / Gateway always expose Data Source URL alongside SFTP/API.
    if (server === 'ngsi-ld' || server === 'gateway') {
      return true;
    }
    const permission = this.ngsildLastPayload()?.payload.basic.permission;
    if (!permission) {
      return true;
    }
    const p = String(permission).trim().toUpperCase();
    return p === 'OPEN' || p === 'PUBLIC';
  }

  async onUploadNowClick(): Promise<void> {
    let server = this.activeUploadResourceServer();
    const itemId = this.ngsildCreatedItemId();
    if (itemId) {
      try {
        const itemResponse = await this.catalogueApi.getItemById(itemId);
        const fromItem = resourceServerIdFromItemDetail(itemResponse);
        if (fromItem) {
          server = fromItem;
        }
      } catch (error) {
        console.warn('Could not load item resource server for upload modal:', error);
      }
    }
    this.uploadModalResourceServer.set(server);
    this.dismissSuccessModal();
    if (this.isOgcFeatureUploadFlow()) {
      this.ogcUploadLoadKey.update((k) => k + 1);
      this.ogcUploadModalVisible.set(true);
    } else if (this.isOgcRasterUploadFlow()) {
      this.stacOnboardingModalVisible.set(true);
    } else {
      this.uploadModalVisible.set(true);
    }
  }

  onUploadModalPrevious(): void {
    if (this.ogcUploadModalVisible()) {
      this.dismissOgcUploadModal(false);
    } else if (this.stacOnboardingModalVisible()) {
      this.dismissStacOnboardingModal(false);
    } else {
      this.dismissUploadModal(false);
    }
    this.successModalVisible.set(true);
  }

  onOgcUploadModalPrevious(): void {
    this.onUploadModalPrevious();
  }

  onStacOnboardingModalPrevious(): void {
    this.onUploadModalPrevious();
  }

  onStacCollectionCreated(): void {
    this.successModalVisible.set(false);
  }

  onOgcUploadPublishSuccess(): void {
    this.ogcUploadModalVisible.set(false);
    this.successModalVisible.set(false);
    this.onUploadPublishSuccess();
  }

  ogcUploadDatasetDescription(): string {
    return (
      this.ngsildLastPayload()?.payload.basic.shortDescription?.trim() ??
      this.ngsildLastPayload()?.payload.basic.longDescription?.trim() ??
      ''
    );
  }

  /** After file-upload / Data Source URL publish APIs succeed. */
  onUploadPublishSuccess(): void {
    this.uploadModalVisible.set(false);
    this.successModalVisible.set(false);
    this.publishSuccessModalVisible.set(true);
  }

  /** After SFTP / API `POST …/auth/asset/request` succeeds (old upload flow). */
  onUploadRequestSuccess(event: { type: NgsildUploadRequestType }): void {
    this.uploadRequestType.set(event.type);
    this.uploadModalVisible.set(false);
    this.successModalVisible.set(false);
    this.uploadRequestSuccessModalVisible.set(true);
  }

  /** Direct Push submit in Connection Details modal — show success, then Create New on OK. */
  onDirectPushSuccess(): void {
    this.uploadModalVisible.set(false);
    this.successModalVisible.set(false);
    this.directPushSuccessModalVisible.set(true);
  }

  dismissDirectPushSuccessModal(): void {
    this.directPushSuccessModalVisible.set(false);
  }

  onDirectPushCreateNew(): void {
    this.dismissDirectPushSuccessModal();
    this.dismissUploadModal();
    this.dismissSuccessModal();
    this.onNgsildBack();
  }

  uploadRequestTypeName(): string {
    const type = this.uploadRequestType();
    if (type === 'SFTP') return 'SFTP request';
    if (type === 'API') return 'API integration';
    return 'request';
  }

  /** Providers need org-admin (metadata) + platform admin; org admins only platform admin. */
  uploadRequestSuccessTitle(): string {
    return this.publisherRole() === 'provider'
      ? 'Request sent for Approval'
      : 'Request sent to Platform Admin';
  }

  uploadRequestSuccessMessage(): string {
    const typeLabel = this.uploadRequestTypeName();
    if (this.publisherRole() === 'provider') {
      return (
        `Your ${typeLabel} has been successfully submitted to the Platform Administrator. ` +
        `Your request is awaiting Organisation Admin approval for metadata and Platform Admin approval.`
      );
    }
    return (
      `Your ${typeLabel} has been successfully submitted to the Platform Administrator. ` +
      `Your request is awaiting approval from the Admin.`
    );
  }

  uploadRequestNextStepsMessage(): string {
    const type = this.uploadRequestType();
    const isProvider = this.publisherRole() === 'provider';
    const orgAdminMetadataStep = isProvider
      ? 'Your Organisation Admin will review the metadata. '
      : '';
    if (type === 'SFTP') {
      return (
        orgAdminMetadataStep +
        "Our team will review your SFTP configuration details. You'll receive an email notification once your SFTP access is approved and set up."
      );
    }
    if (type === 'API') {
      return (
        orgAdminMetadataStep +
        "Our team will review your API integration details. You'll receive an email notification once your API integration is approved and set up."
      );
    }
    return orgAdminMetadataStep.trim();
  }

  dismissUploadRequestSuccessModal(): void {
    this.uploadRequestSuccessModalVisible.set(false);
    this.uploadRequestType.set(null);
  }

  onUploadRequestViewDataset(): void {
    this.dismissUploadRequestSuccessModal();
    this.dismissSuccessModal();
    this.onViewDatasetClick();
  }

  onUploadRequestCreateNew(): void {
    this.dismissUploadRequestSuccessModal();
    this.dismissSuccessModal();
    this.onNgsildBack();
  }

  publishSuccessTitle(): string {
    return 'Dataset Submitted for Approval';
  }

  publishSuccessMessage(): string {
    return this.publisherRole() === 'provider'
      ? 'Your dataset request has been submitted and is awaiting Organisation Admin approval for metadata and platform admin approval.'
      : 'Your dataset request has been submitted and is awaiting platform admin approval.';
  }

  onPublishViewDataset(): void {
    this.publishSuccessModalVisible.set(false);
    this.dismissUploadModal();
    this.dismissSuccessModal();
    this.onViewDatasetClick();
  }

  onPublishCreateNew(): void {
    this.publishSuccessModalVisible.set(false);
    this.dismissUploadModal();
    this.dismissSuccessModal();
    this.onNgsildBack();
  }

  onUploadLaterClick(): void {
    this.dismissSuccessModal();
    /** Return to Add Dataset New landing (dataset / server selection); clears NGSI-LD wizard query params */
    this.onNgsildBack();
  }

  onViewDatasetClick(): void {
    const itemId =
      this.ngsildCreatedItemId() ?? this.ngsildLastPayload()?.itemId ?? null;
    if (!itemId) {
      void this.router.navigate([this.catalogueDatasetsPath()]);
      this.dismissSuccessModal();
      return;
    }
    const base = this.datasetDetailPath().replace(/\/$/, '');
    void this.router.navigate([base, itemId]);
    this.dismissSuccessModal();
  }
}
